import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { RequestsService } from '@shared/providers/external/requests.service';
import { AlertService } from '@shared/providers/utilities/alert.service';
import { LoaderService } from '@shared/providers/utilities/loader.service';

interface IChangePasswordResponse {
  Succeeded: boolean;
  Code: string;
}

@Component({
  selector: 'anp-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false
})
export class ChangePasswordComponent {
  public formData: FormGroup = this.fb.group(
    {
      currentPassword: ['', [Validators.required]],
      newPassword: [
        '',
        [Validators.required, Validators.minLength(8), Validators.pattern(/^(?=.*[A-Z])(?=.*\d).+$/)]
      ],
      confirmPassword: ['', [Validators.required]]
    },
    { validators: this.matchPasswords }
  );

  public showPasswords = { current: false, new: false, confirm: false };
  public isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private modalCtrl: ModalController,
    private requestsService: RequestsService,
    private alertService: AlertService,
    private loaderService: LoaderService
  ) {}

  public get currentPassword(): AbstractControl<string> {
    return this.formData?.get('currentPassword');
  }

  public get newPassword(): AbstractControl<string> {
    return this.formData?.get('newPassword');
  }

  public get confirmPassword(): AbstractControl<string> {
    return this.formData?.get('confirmPassword');
  }

  public get passwordsMismatch(): boolean {
    return this.formData?.errors?.['mismatch'] && this.confirmPassword?.dirty;
  }

  public toggle(field: 'current' | 'new' | 'confirm'): void {
    this.showPasswords[field] = !this.showPasswords[field];
  }

  public async dismiss(): Promise<void> {
    await this.modalCtrl.dismiss();
  }

  public async submit(): Promise<void> {
    if (this.formData.invalid || this.isSubmitting) return;
    this.isSubmitting = true;

    const loader = await this.loaderService.simpleLoader();
    try {
      const result = await this.requestsService.changePassword<IChangePasswordResponse>({
        CurrentPassword: this.currentPassword.value,
        NewPassword: this.newPassword.value
      });

      await loader.dismiss();

      if (!result.Succeeded) {
        await this.showErrorMessage(result.Code);
        return;
      }

      await this.alertService.simpleAlert('Éxito', 'Contraseña cambiada correctamente');
      await this.modalCtrl.dismiss();
    } catch (error) {
      console.error(error);
      await loader.dismiss();
      await this.alertService.simpleAlert('Error', 'Ha ocurrido un error');
    } finally {
      this.isSubmitting = false;
    }
  }

  private matchPasswords(group: AbstractControl): ValidationErrors | null {
    const newPwd = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (!newPwd || !confirm) return null;
    return newPwd === confirm ? null : { mismatch: true };
  }

  private async showErrorMessage(code: string): Promise<void> {
    switch (code) {
      case 'RS1':
        await this.alertService.simpleAlert('Error', 'No hemos podido encontrar tu usuario');
        break;
      case 'RS2':
        await this.alertService.simpleAlert('Error', 'Contraseña incorrecta');
        break;
      default:
        await this.alertService.simpleAlert('Error', 'Ha ocurrido un error');
        break;
    }
  }
}
